import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxmTriggerV1TriggerQueryServiceTriggerGet } from '@interxfi/api-client/generated/hooks/ixm-trigger-v1-trigger-query-service-trigger-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxmTriggerV1TriggerState } from '@interxfi/types';
import { TriggerBreadcrumb } from '../../../../../../../../../features/meta/trigger/trigger-breadcrumb';
import { TriggerEntityMenu } from '../../../../../../../../../features/meta/trigger/trigger-entity-menu';
import { TriggerEventSummary } from '../../../../../../../../../features/meta/trigger/trigger-event';
import { getTriggerStateListItems } from '../../../../../../../../../features/meta/trigger/trigger-list-items';
import { getTriggerDetailPath } from '../../../../../../../../../features/meta/trigger/trigger-routing';
import { TriggerStatusPill } from '../../../../../../../../../features/meta/trigger/trigger-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/trigger/_trigger/$triggerId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `cc-trigger-${params.triggerId}`,
        label: <TriggerBreadcrumb triggerId={params.triggerId} />,
        to: getTriggerDetailPath(params.triggerId),
        suffix: <CopyButton content={params.triggerId} label="Copy Trigger ID" tooltip={{ copy: params.triggerId, copied: 'Copied Trigger ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Trigger'),
  component: TriggerDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load trigger', 'view this trigger');

function getListItems(data: IxmTriggerV1TriggerState | undefined) {
  const items = getTriggerStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [items.data.triggerName, items.data.appName, items.data.cron, items.data.target],
      },
      ...match(items.data.payload)
        .with({ static: P.not(P.nullish) }, ({ static: staticPayload, typeName }) => [
          {
            id: 'payload',
            title: 'Payload',
            items: [typeName, staticPayload.value.static],
          },
        ])
        .otherwise(() => []),
    ] as const satisfies ReadonlyListGroup[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.triggerId, items.data.lastTick] as const satisfies ReadonlyListItem[],
  } as const;
}

function TriggerDetail() {
  const { triggerId } = Route.useParams();
  const request = { triggerId } as const;
  const { data, error, isPending } = useIxmTriggerV1TriggerQueryServiceTriggerGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.trigger), [data?.trigger]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <TriggerStatusPill isPending={showPending} size="large" status={data?.trigger?.status} />
          </HorizontalStack>
          <TriggerEntityMenu triggerId={triggerId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <TriggerEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
